// usePrintPortal.ts
import { useCallback, useRef } from "react";

/**
 * Prints a specific DOM node by cloning it into a standalone container
 * appended directly to document.body, bypassing any parent MUI Dialog/Modal
 * fixed-position wrappers that cause extra blank pages when printed.
 */
export function usePrintPortal(printAreaId: string) {
  const cleanupRef = useRef<(() => void) | null>(null);

  const print = useCallback(() => {
    const source = document.getElementById(printAreaId);
    if (!source) {
      window.print();
      return;
    }

    const container = document.createElement("div");
    container.id = "print-portal-root";
    const clone = source.cloneNode(true) as HTMLElement;
    container.appendChild(clone);
    document.body.appendChild(container);

    const style = document.createElement("style");
    style.id = "print-portal-style";
    style.textContent = `
      @media print {
        body > *:not(#print-portal-root) { display: none !important; }
        #print-portal-root {
          position: static !important;
          display: block !important;
          width: 100% !important;
        }
      }
    `;
    document.head.appendChild(style);

    const cleanup = () => {
      container.remove();
      style.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    cleanupRef.current = cleanup;
    window.addEventListener("afterprint", cleanup);

    // Give the browser a tick to paint the cloned node before printing
    requestAnimationFrame(() => window.print());
  }, [printAreaId]);

  return print;
}
